import React, { useCallback, useState } from "react";
import {
  BooleanParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";
import classnames from "classnames";

import TemplateCard from "../components/templates/TemplateCard";
import useTranslation from "../hooks/useTranslations";
import useViewer from "../hooks/useViewer";
import { SortableVariables } from "../graphql/types";
import {
  TemplateIcon,
  SortAscendingDirectionIcon,
  SortDescendingDirectionIcon,
  StarIcon,
} from "../components/icons";
import {
  Container,
  ScreenContainer,
  ScreenHeader,
  Link,
  EmptyState,
  H6,
  ButtonGroup,
  Button,
} from "../components/wombat-ui";

import List from "../components/utils/List";
import Pagination from "../components/utils/Pagination";
import SearchInput from "../components/utils/SearchInput";

import useNotification from "../hooks/useNotification";
import useConfirm from "../components/utils/confirm/useConfirm";
import { useBreadcrumbs } from "../hooks/useBreadcrumbs";
import { TemplateFragment, useDeleteTemplate, useGetTemplates, useInsertTemplate } from '../graphql/generated/definitions';

const StarIconPlain = ({ className, ...props }) => {
  const plainIconClassName = classnames(className, "fill-current");

  return <StarIcon {...props} className={plainIconClassName} />;
};

const TemplateListScreen = () => {
  const t = useTranslation();
  const { isCapableOfInsertingTemplates } = useViewer();

  useBreadcrumbs([
    {
      to: "/templates",
      text: t("templates.templates") as string,
    },
  ]);

  const [{ page, q, sortBy, direction, favorite }, setQuery] = useQueryParams({
    page: NumberParam,
    sortBy: StringParam,
    direction: withDefault(StringParam, "ASC"),
    favorite: BooleanParam,
    q: StringParam,
  });

  const { data, loading, refetch } = useGetTemplates({
    variables: {
      reviewId: null,
      sortBy:
        (sortBy as SortableVariables<TemplateFragment>) ||
        "createdAtWithFavoriteFirst",
      direction: direction as "ASC" | "DESC",
      q,
      areFavorite: favorite ? favorite : undefined,
      per: 24,
      page: page || 1,
    },
    fetchPolicy: "cache-and-network",
  });

  const templates = data?.templates?.collection || [];
  const itemsCount = data?.templates?.metadata?.totalCount;

  const [deleteTemplate] = useDeleteTemplate();
  const [updateTemplate] = useInsertTemplate();
  const confirm = useConfirm();
  const { notifySuccess, notifyError } = useNotification();

  const handleDeleteTemplate = useCallback(
    (template) => {
      confirm({
        title: t("templates.delete"),
        message: t("templates.deleteWarning"),
        buttons: [
          { text: t("forms.actions.cancel") },
          {
            colorScheme: "red",
            text: t("forms.actions.delete"),
            id: "delete-template",
            onClick: async () => {
              try {
                await deleteTemplate({
                  variables: { input: { id: template.id } },
                });

                refetch();

                notifySuccess({
                  title: t("templates.notification.delete.success") as string,
                });
              } catch (e) {
                notifyError({
                  title: t("templates.notification.delete.error") as string,
                });
              }
            },
          },
        ],
      });
    },
    [deleteTemplate, refetch]
  );

  const handleUpdateTemplateFavorite = useCallback(
    async (template: TemplateFragment) => {
      try {
        await updateTemplate({
          variables: {
            input: { id: template.id, favorite: template.favorite },
          },
        });
      } catch (e) {
        notifyError({
          title: t("templates.notification.delete.error") as string,
        });
      }
    },
    [updateTemplate]
  );

  const [search, setSearch] = useState(q);

  return (
    <ScreenContainer
      error={
        !isCapableOfInsertingTemplates ? (
          <EmptyState
            title={t("network.unauthorized") as string}
            action={
              <Link variant="button" to="/">
                {t("network.backToHome")}
              </Link>
            }
          />
        ) : undefined
      }
    >
      <ScreenHeader
        title={t("templates.templates") as string}
        description={t("templates.templatesDescription") as string}
        icon={TemplateIcon}
        actions={[
          isCapableOfInsertingTemplates && (
            <Link variant="button" to="/templates/new" key="new">
              {t("templates.createTemplate")}
            </Link>
          ),
        ]}
      />
      <Container className="flex flex-col space-y-10">
        <List
          isLoading={loading}
          emptyMessage={t("templates.noMatchingTemplates") as string}
          data={templates}
          cardPerRow={3}
          renderAsCard={(data) => (
            <TemplateCard
              key={data.id}
              template={data}
              onDelete={handleDeleteTemplate}
              onFavorite={handleUpdateTemplateFavorite}
            />
          )}
          cardOnly
          id="templates-list"
        >
          <SearchInput
            onSubmit={(q) => setQuery({ page: 1, q })}
            label={t("list.search") as string}
            placeholder={t("templates.searchPlaceholder") as string}
            value={search}
            onChange={setSearch}
          />
          <div className="flex space-x-3">
            <Button
              variant={favorite ? "primary" : "subtle"}
              onClick={() => setQuery({ favorite: !favorite })}
              icon={favorite ? StarIconPlain : StarIcon}
            />
            <ButtonGroup scale="sm">
              <Button
                icon={SortAscendingDirectionIcon}
                onClick={() => setQuery({ direction: "ASC" })}
                variant={
                  direction === "ASC" || !direction ? "primary" : undefined
                }
              />
              <Button
                icon={SortDescendingDirectionIcon}
                onClick={() => setQuery({ direction: "DESC" })}
                variant={direction === "DESC" ? "primary" : undefined}
              />
            </ButtonGroup>
          </div>
        </List>
        {!loading && (
          <Pagination
            page={page}
            onChange={(page) => setQuery({ page })}
            itemsCount={itemsCount}
            itemsPerPage={24}
          />
        )}
      </Container>
    </ScreenContainer>
  );
};

export default TemplateListScreen;
